import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getCategories, getMenu } from '../lib/api';
import axios from 'axios';
import { Plus, Minus, Trash2, ShoppingBag, CheckCircle2, UtensilsCrossed } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TableOrderPage() {
  const { tableNumber } = useParams();
  const { t, getLocalizedField } = useLanguage();
  const tn = parseInt(tableNumber);

  const [tableInfo, setTableInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/tables/${tn}/info`).then(r => setTableInfo(r.data)).catch(() => setError('Tisch nicht gefunden')),
      getCategories().then(r => setCategories(r.data)),
      getMenu().then(r => setMenuItems(r.data)),
    ]).finally(() => setLoading(false));
  }, [tn]);

  const addItem = (item) => {
    setCart(prev => {
      const ex = prev.find(i => i.id === item.id);
      if (ex) return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) setCart(prev => prev.filter(i => i.id !== id));
    else setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
  };

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = cart.reduce((s, i) => s + i.quantity, 0);
  const filtered = activeCategory === 'all' ? menuItems : menuItems.filter(i => i.category_id === activeCategory);

  const handleOrder = async () => {
    if (cart.length === 0) return;
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/tables/${tn}/orders`, {
        table_number: tn,
        items: cart.map(i => ({ item_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        notes, total
      });
      setOrderSuccess(true); setCart([]);
    } catch { setError('Bestellung konnte nicht aufgegeben werden.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center"><div className="animate-pulse">{t('loading')}</div></div>;

  if (error && !tableInfo) return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
      <div className="text-center"><UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-[hsl(16,55%,36%)]" /><h2 className="font-[family-name:var(--font-display)] text-xl mb-2">Tisch nicht gefunden</h2><p className="text-sm text-[hsl(22,10%,38%)]">Bitte scannen Sie den QR-Code erneut.</p></div>
    </div>
  );

  if (orderSuccess) return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-sm">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(97,18%,34%)]/10 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-[hsl(97,18%,34%)]" /></div>
        <h2 className="font-[family-name:var(--font-display)] text-2xl mb-3">Bestellung aufgegeben!</h2>
        <p className="text-[hsl(22,10%,38%)] mb-2">Ihre Bestellung für Tisch {tn} wurde erfolgreich übermittelt.</p>
        <p className="text-sm text-[hsl(22,10%,38%)] mb-6">Wir bereiten Ihre Gerichte mit Liebe zu.</p>
        <button onClick={() => { setOrderSuccess(false); setShowCheckout(false); }} className="px-6 py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white hover:brightness-[1.05] transition-colors duration-200">Weitere Bestellung aufgeben</button>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)] pb-24">
      {/* Header */}
      <div className="bg-[hsl(20,17%,12%)] text-white py-6 px-4 text-center">
        <p className="text-xs text-[hsl(42,72%,44%)] uppercase tracking-wider mb-1">Little Italy Ristorante</p>
        <h1 className="font-[family-name:var(--font-display)] text-2xl">Tisch {tn}</h1>
        <p className="text-white/60 text-sm mt-1">Bestellen Sie direkt an Ihrem Tisch</p>
      </div>

      <AnimatePresence mode="wait">
        {showCheckout ? (
          <motion.div key="checkout" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="px-4 py-6 max-w-lg mx-auto">
            <button onClick={() => setShowCheckout(false)} className="text-sm text-[hsl(22,10%,38%)] mb-4 hover:text-[hsl(16,55%,36%)]">← Zurück zur Speisekarte</button>
            <div className="bg-white rounded-2xl border border-[hsl(32,17%,84%)] p-6 space-y-4">
              <h2 className="font-[family-name:var(--font-display)] text-lg">Ihre Bestellung – Tisch {tn}</h2>
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              <div className="space-y-2">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center gap-3 py-2 border-b border-[hsl(32,17%,84%)]/50 last:border-0">
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{item.name}</p><p className="text-xs text-[hsl(22,10%,38%)]">{item.price.toFixed(2)} €</p></div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(item.id, item.quantity - 1)} className="w-7 h-7 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-xs font-medium">{item.quantity}</span>
                      <button onClick={() => updateQty(item.id, item.quantity + 1)} className="w-7 h-7 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      <button onClick={() => updateQty(item.id, 0)} className="w-7 h-7 rounded text-red-500 flex items-center justify-center ml-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Anmerkungen (optional)" className="w-full px-3 py-2 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] text-sm resize-none" />
              <div className="flex justify-between items-center pt-2 border-t border-[hsl(32,17%,84%)]">
                <span className="font-semibold">{t('total')}</span>
                <span className="font-[family-name:var(--font-display)] text-xl text-[hsl(16,55%,36%)]">{total.toFixed(2)} €</span>
              </div>
              <button onClick={handleOrder} disabled={submitting} className="w-full py-3.5 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white hover:brightness-[1.05] transition-colors duration-200 disabled:opacity-50">
                {submitting ? 'Wird gesendet...' : `Bestellung aufgeben – ${total.toFixed(2)} €`}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Category tabs */}
            <div className="sticky top-0 z-30 bg-[hsl(38,44%,96%)]/95 backdrop-blur-md border-b border-[hsl(32,17%,84%)] px-4">
              <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
                <button onClick={() => setActiveCategory('all')} className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium ${activeCategory === 'all' ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-white border border-[hsl(32,17%,84%)]'}`}>Alle</button>
                {categories.map(cat => (
                  <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium ${activeCategory === cat.id ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-white border border-[hsl(32,17%,84%)]'}`}>{getLocalizedField(cat, 'name')}</button>
                ))}
              </div>
            </div>
            {/* Items */}
            <div className="px-4 py-4 space-y-2 max-w-lg mx-auto">
              {filtered.map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-[hsl(32,17%,84%)] p-3 flex items-center gap-3">
                  {item.image && <img src={item.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" loading="lazy" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate">{getLocalizedField(item, 'name')}</h3>
                    {getLocalizedField(item, 'description') && <p className="text-[10px] text-[hsl(22,10%,38%)] line-clamp-1">{getLocalizedField(item, 'description')}</p>}
                  </div>
                  <span className="font-bold text-sm text-[hsl(16,55%,36%)] whitespace-nowrap">{item.price.toFixed(2)} €</span>
                  <button onClick={() => addItem({ id: item.id, name: getLocalizedField(item, 'name'), price: item.price, image: item.image })} className="w-8 h-8 rounded-lg bg-[hsl(16,55%,36%)] text-white flex items-center justify-center flex-shrink-0"><Plus className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed bottom bar */}
      {cart.length > 0 && !showCheckout && !orderSuccess && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[hsl(32,17%,84%)] p-4 z-40">
          <button onClick={() => setShowCheckout(true)} className="w-full py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white flex items-center justify-center gap-2">
            <ShoppingBag className="w-4 h-4" /> Bestellung prüfen ({itemCount}) – {total.toFixed(2)} €
          </button>
        </div>
      )}
    </div>
  );
}
